/**
 * MinimizedPillDock Component
 *
 * Bottom-right container for collapsed panel pills.
 * Pills stack VERTICALLY ABOVE the position where + button would be.
 * Click to restore panel with spring animation.
 * Respects safe-area insets for notched devices.
 */

import React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { Target, Flame, Timer } from 'lucide-react';
import type { PanelType, PanelConfig, PanelIconType } from '../../types/weeklyPanels';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import { useUserStickiesStore } from '@shared/store/useUserStickiesStore';
import { useAppPanelStore } from '@shared/store/useAppPanelStore';
import { APP_PANEL_TASKBAR_HEIGHT } from '@shared/components/layouts/AppPanelTaskbar';

/**
 * Map icon type to Lucide component
 */
const IconMap: Record<PanelIconType, React.FC<{ size?: number; className?: string }>> = {
  target: Target,
  flame: Flame,
  timer: Timer,
};

interface MinimizedPillProps {
  config: PanelConfig;
  onClick: () => void;
}

interface MinimizedPillDockProps {
  collapsedPanels: Array<{ type: PanelType; config: PanelConfig }>;
  onRestore: (panelType: PanelType) => void;
}

/**
 * Animation variants for pill - animates from below (y: 20) to position
 */
const pillVariants: Variants = {
  hidden: { opacity: 0, scale: 0, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 400, damping: 25 },
  },
  exit: { opacity: 0, scale: 0, y: 20, transition: { duration: 0.15 } },
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
};

/**
 * Reduced motion variants
 */
const reducedMotionVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
  hover: {},
  tap: {},
};

/**
 * Individual minimized pill button
 * Always shows icon-only (no title text) on both mobile and desktop
 */
const MinimizedPill: React.FC<MinimizedPillProps> = ({ config, onClick }) => {
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const variants = prefersReducedMotion ? reducedMotionVariants : pillVariants;
  const IconComponent = IconMap[config.colors.icon];

  return (
    <motion.button
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="exit"
      whileHover="hover"
      whileTap="tap"
      onClick={onClick}
      className={`
        ${config.colors.bg} ${config.colors.border}
        border-2 rounded-full shadow-md
        flex items-center justify-center
        p-3
        cursor-pointer
        focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
        transition-shadow hover:shadow-lg
        ${isMobile ? 'min-h-[44px] min-w-[44px]' : ''}
      `}
      aria-label={`Restore ${config.title}`}
      aria-expanded={false}
      data-testid={`pill-${config.type}`}
    >
      <IconComponent size={18} className={config.colors.iconColor} aria-hidden="true" />
    </motion.button>
  );
};

/**
 * Dock container for minimized pills
 * Pills stack VERTICALLY ABOVE the UserStickyPillDock (bottom-right)
 *
 * Layout: Uses flex-col-reverse so pills stack upward from the bottom:
 *   - First collapsed panel appears closest to the UserStickyPillDock
 *   - Additional pills stack above
 *   - Gap of 8px (gap-2) between pills
 *   - Bottom position accounts for UserStickyPillDock (bottom-4 + 44px height + gap)
 */
export const MinimizedPillDock: React.FC<MinimizedPillDockProps> = ({
  collapsedPanels,
  onRestore,
}) => {
  const isMobile = useIsMobile();
  const dockDisplayMode = useUserStickiesStore((s) => s.dockDisplayMode);
  const isTaskbarVisible = useAppPanelStore((s) => s.isTaskbarVisible);

  // Hide when sticky dock chevron is hidden.
  if (dockDisplayMode === 'hidden') {
    return null;
  }

  if (collapsedPanels.length === 0) {
    return null;
  }

  // Stack vertically above the UserStickyPillDock
  // UserStickyPillDock is at bottom-4 (16px), button height 44px, plus gap (8px) = 68px
  // Add taskbar height when visible
  const baseBottom = 68;
  const taskbarOffset = isTaskbarVisible ? APP_PANEL_TASKBAR_HEIGHT : 0;
  const bottomPosition = baseBottom + taskbarOffset;

  return (
    <div
      className="fixed z-50 flex flex-col-reverse items-end gap-2 right-4 transition-[bottom] duration-200 ease-out"
      style={{
        bottom: isMobile
          ? `calc(${bottomPosition}px + env(safe-area-inset-bottom))`
          : `${bottomPosition}px`,
      }}
      role="group"
      aria-label="Minimized panels"
    >
      <AnimatePresence mode="popLayout">
        {collapsedPanels.map(({ type, config }) => (
          <MinimizedPill
            key={type}
            config={config}
            onClick={() => onRestore(type)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};
